import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  catchError,
  EMPTY,
  first,
  Observable,
  retryWhen,
  Subject,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';

import { ComponentStore } from '@ngrx/component-store';

import { Account } from '@personal-area/global/accounts/models';
import { AccountService, AccountSocketService } from '@personal-area/global/accounts/services';
import { ApiFormService } from '@personal-area/global/form-validation/services';
import { retryStrategy } from '@personal-area/global/http/services';

import { getAccountType } from '../../pipes/account-type.pipe';

export interface SwapFreeComponentState {
  isChecked: boolean;
  isLoading: boolean;
  isDisabled: boolean;
}

@Injectable()
export class SwapFreeComponentStore
  extends ComponentStore<SwapFreeComponentState>
  implements OnDestroy
{
  public readonly isChecked = this.select((state) => state.isChecked);
  public readonly isLoading = this.select((state) => state.isLoading);
  public readonly isDisabled = this.select((state) => state.isDisabled);

  public readonly setup = this.updater(
    (state: SwapFreeComponentState, account: Account) => ({
      isChecked: account.isSwapFree,
      isLoading: false,
      isDisabled: getAccountType(account) === 'archived',
    })
  );

  public readonly toggle = this.effect((account$: Observable<Account>) =>
    account$.pipe(
      withLatestFrom(this.state$),
      tap(([, { isChecked }]) => this._block(!isChecked)),
      switchMap(([{ id, userId }, { isChecked: oldValue }]) =>
        this._accountService.updateSwapfreeSetting(userId, id, !oldValue).pipe(
          retryWhen(retryStrategy()),
          catchError((error: HttpErrorResponse) => {
            this._unblock(oldValue);
            this._formService.defaultFormError(error);
            return EMPTY;
          }),
          switchMap(() => this._accountSocket.accountSwapFree),
          first(),
          tap(({ isSwapFree }) => this._unblock(isSwapFree)),
          takeUntil(this._unsubscribe)
        )
      )
    )
  );

  private _unsubscribe = new Subject<void>();

  constructor(
    private _accountService: AccountService,
    private _accountSocket: AccountSocketService,
    private _formService: ApiFormService,
  ) {
    super({
      isChecked: false,
      isLoading: true,
      isDisabled: true,
    });
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  private _block(isChecked: boolean): void {
    this.setState({
      isChecked,
      isLoading: true,
      isDisabled: true,
    });
  }

  private _unblock(isChecked: boolean): void {
    this.setState({
      isChecked,
      isLoading: false,
      isDisabled: false,
    });
  }
}
